import type {
  SavedCircuitConfigurationIdentity,
  SavedCircuitLapSet,
  TrackContribution,
} from '@kangaroo/shared';
import {
  buildSavedCircuitConfigurationIdentity,
  savedCircuitConfigurationKey,
  savedCircuitConfigurationsMatch,
  trackContributionHasGeometry,
} from '@kangaroo/shared';

import { startLineFromDetailedGeometry } from './detailedCircuitGeometry';
import { trackDistanceKm, venuesAlign } from './trackGeo';
import {
  buildMapPointsForGpsWindow,
  downsampleTrackPoints,
  lapWindow,
  projectTrackPointsWithReferenceBounds,
  racingLaps,
  type MyChronSessionExport,
  type TrackMapMarker,
  type TrackMapPoint,
} from './telemetrySession';
import {
  geometryFromCircuitDraft,
  healCircuitPolylineForDisplay,
  matchSessionToCircuitDraft,
  type GpsCoord,
} from './trackEnrichment';

export type CircuitMapOverlay = {
  contributionId: string;
  trackId: string;
  name: string;
  configuration?: SavedCircuitConfigurationIdentity | null;
  referenceTrackGroups: TrackMapPoint[][];
  mapMarkers: TrackMapMarker[];
  startFinishLine?: { pointA: GpsCoord; pointB: GpsCoord };
};

export function savedCircuitLapSetForOverlay(
  savedCircuitLapSet: SavedCircuitLapSet | null | undefined,
  circuitOverlay: Pick<CircuitMapOverlay, 'contributionId' | 'configuration'> | null,
): SavedCircuitLapSet | null {
  if (
    !savedCircuitLapSet ||
    !circuitOverlay ||
    savedCircuitLapSet.contributionId !== circuitOverlay.contributionId ||
    !savedCircuitConfigurationKey(savedCircuitLapSet.configuration) ||
    !savedCircuitConfigurationKey(circuitOverlay.configuration) ||
    !savedCircuitConfigurationsMatch(savedCircuitLapSet.configuration, circuitOverlay.configuration)
  ) {
    return null;
  }

  return savedCircuitLapSet;
}

function coordsToReferenceMapPoints(coords: GpsCoord[], maxPoints = 120): TrackMapPoint[] {
  const healed = healCircuitPolylineForDisplay(coords, 2.5, 400);
  const sampled = downsampleTrackPoints(healed, maxPoints) as GpsCoord[];
  if (sampled.length === 0) {
    return [];
  }

  const gps = sampled.map((coord, index) => ({
    lat: coord.lat,
    lon: coord.lon,
    alt: 0,
    speed: 42 / 3.6,
    timecodeMs: index * 100,
    fix: 3,
    satellites: 10,
  }));

  return projectTrackPointsWithReferenceBounds(gps, gps, 1200);
}

function buildSessionReferenceOutline(session: MyChronSessionExport): TrackMapPoint[] | null {
  const laps = racingLaps(session.laps);
  if (laps.length === 0) {
    return null;
  }

  const bestLap = laps.reduce((best, lap) => (lap.durationMs < best.durationMs ? lap : best));
  const { startMs, endMs } = lapWindow(bestLap);
  const points = buildMapPointsForGpsWindow(session, startMs, endMs, 600);
  return points.length >= 2 ? points : null;
}

function projectMarkerOnOutline(
  lat: number,
  lon: number,
  outline: TrackMapPoint[],
): { x: number; y: number } {
  if (outline.length === 0) {
    return { x: 0, y: 0 };
  }

  let nearest = outline[0]!;
  let bestDistance = Number.POSITIVE_INFINITY;
  for (const point of outline) {
    const distance = (point.lat - lat) ** 2 + (point.lon - lon) ** 2;
    if (distance < bestDistance) {
      bestDistance = distance;
      nearest = point;
    }
  }

  return { x: nearest.x, y: nearest.y };
}

export function buildCircuitMapOverlay(
  contribution: TrackContribution,
  session?: MyChronSessionExport | null,
): CircuitMapOverlay | null {
  const payload = contribution.payload;
  if (!payload.gpsPolyline || payload.gpsPolyline.length < 2) {
    return null;
  }

  const geometry = geometryFromCircuitDraft(payload);
  const outlinePoints =
    (session ? buildSessionReferenceOutline(session) : null) ??
    coordsToReferenceMapPoints(geometry.polyline);
  const mapMarkers: TrackMapMarker[] = [];

  const suggestedStartLine = payload.startLineSuggestion;
  const suggestionIsUsable =
    suggestedStartLine?.pointA &&
    suggestedStartLine?.pointB &&
    (suggestedStartLine.pointA.lat !== suggestedStartLine.pointB.lat ||
      suggestedStartLine.pointA.lon !== suggestedStartLine.pointB.lon);
  const startLine = suggestionIsUsable
    ? suggestedStartLine
    : startLineFromDetailedGeometry(payload.detailedGeometry);
  let startFinishLine: CircuitMapOverlay['startFinishLine'];
  if (startLine?.pointA && startLine?.pointB) {
    startFinishLine = startLine;
    const startMid = {
      lat: (startLine.pointA.lat + startLine.pointB.lat) / 2,
      lon: (startLine.pointA.lon + startLine.pointB.lon) / 2,
    };
    const projected = projectMarkerOnOutline(startMid.lat, startMid.lon, outlinePoints);
    mapMarkers.push({
      id: 'circuit-sf',
      kind: 'sector-boundary',
      label: 'Start',
      lat: startMid.lat,
      lon: startMid.lon,
      x: projected.x,
      y: projected.y,
      color: '#22C55E',
      selected: true,
    });
  }

  for (const corner of geometry.cornerApexSuggestions) {
    const projected = projectMarkerOnOutline(corner.lat, corner.lon, outlinePoints);
    mapMarkers.push({
      id: `circuit-turn-${corner.id}`,
      kind: 'note',
      label: `T${corner.turnNumber}`,
      lat: corner.lat,
      lon: corner.lon,
      x: projected.x,
      y: projected.y,
      color: '#60A5FA',
    });
  }

  return {
    contributionId: contribution.id,
    trackId: contribution.trackId,
    name: contribution.venue?.trim() || 'Saved circuit',
    configuration: startFinishLine
      ? buildSavedCircuitConfigurationIdentity(contribution, startFinishLine)
      : null,
    referenceTrackGroups: outlinePoints.length >= 2 ? [outlinePoints] : [],
    mapMarkers,
    startFinishLine,
  };
}

export function findTrackContributionById(
  contributions: TrackContribution[] | undefined,
  contributionId: string | null,
): TrackContribution | null {
  if (!contributionId || !contributions?.length) {
    return null;
  }

  return contributions.find((contribution) => contribution.id === contributionId) ?? null;
}

export type CircuitOverlaySource = 'url' | 'assigned' | 'saved-laps' | 'track' | 'gps-match';

export type ResolvedCircuitOverlay = {
  overlay: CircuitMapOverlay | null;
  source: CircuitOverlaySource | null;
  contributionId: string | null;
};

export function circuitOverlaySourceLabel(source: CircuitOverlaySource | null) {
  switch (source) {
    case 'url':
      return 'From link';
    case 'assigned':
      return 'Assigned to session';
    case 'saved-laps':
      return 'Saved lap set';
    case 'track':
      return 'Best match for track';
    case 'gps-match':
      return 'GPS auto-match';
    default:
      return null;
  }
}

function contributionHasOverlayGeometry(contribution: TrackContribution) {
  return (
    trackContributionHasGeometry(contribution.payload) ||
    Boolean(
      contribution.payload.detailedGeometryVersion ||
      contribution.payload.detailedGeometryChunkCount ||
      contribution.payload.detailedGeometry,
    )
  );
}

export function findBestContributionForTrack(
  contributions: TrackContribution[] | undefined,
  trackId: string | null | undefined,
): TrackContribution | null {
  if (!trackId || !contributions?.length) {
    return null;
  }

  const candidates = contributions.filter(
    (contribution) =>
      contribution.trackId === trackId && contributionHasOverlayGeometry(contribution),
  );
  if (candidates.length === 0) {
    return null;
  }

  const statusRank: Record<TrackContribution['status'], number> = {
    merged: 0,
    accepted: 1,
    reviewed: 2,
    pending: 3,
    draft: 4,
    rejected: 5,
  };

  return (
    [...candidates].sort((left, right) => {
      const rankDiff = statusRank[left.status] - statusRank[right.status];
      if (rankDiff !== 0) {
        return rankDiff;
      }

      return Date.parse(right.updatedAt) - Date.parse(left.updatedAt);
    })[0] ?? null
  );
}

function normalizeCircuitVenueName(value: string | null | undefined) {
  return value?.trim().toLowerCase() || null;
}

function gpsCenterFromPoints(
  gps: Array<{ lat: number; lon: number }> | null | undefined,
): { lat: number; lon: number } | null {
  if (!gps?.length) {
    return null;
  }

  const sum = gps.reduce((acc, point) => ({ lat: acc.lat + point.lat, lon: acc.lon + point.lon }), {
    lat: 0,
    lon: 0,
  });

  return {
    lat: sum.lat / gps.length,
    lon: sum.lon / gps.length,
  };
}

function sessionPickerCenter(options: {
  session?: MyChronSessionExport | null;
  sessionGps?: Array<{ lat: number; lon: number }> | null;
}) {
  if (options.session) {
    return sessionGpsCenter(options.session);
  }

  return gpsCenterFromPoints(options.sessionGps);
}

function sessionGpsCenter(session: MyChronSessionExport): { lat: number; lon: number } | null {
  if (!session.gps?.length) {
    return null;
  }

  const sum = session.gps.reduce(
    (acc, point) => ({ lat: acc.lat + point.lat, lon: acc.lon + point.lon }),
    { lat: 0, lon: 0 },
  );

  return {
    lat: sum.lat / session.gps.length,
    lon: sum.lon / session.gps.length,
  };
}

function findNearestContributionByLocation(
  contributions: TrackContribution[],
  center: { lat: number; lon: number },
  maxDistanceKm = 8,
): TrackContribution | null {
  let nearest: TrackContribution | null = null;
  let nearestDistanceKm = Number.POSITIVE_INFINITY;

  for (const contribution of contributions) {
    const location = contribution.location;
    if (!location) {
      continue;
    }

    const distanceKm = trackDistanceKm(
      {
        id: contribution.trackId,
        teamId: contribution.teamId,
        name: contribution.venue ?? '',
        centerLat: location.centerLat,
        centerLon: location.centerLon,
        createdAt: contribution.createdAt,
        updatedAt: contribution.updatedAt,
      },
      center.lat,
      center.lon,
    );

    if (distanceKm <= maxDistanceKm && distanceKm < nearestDistanceKm) {
      nearest = contribution;
      nearestDistanceKm = distanceKm;
    }
  }

  return nearest;
}

export function suggestCircuitContributionForSession(options: {
  session: MyChronSessionExport | null;
  contributions: TrackContribution[] | undefined;
  sessionTrackName?: string | null;
  trackId?: string | null;
}): TrackContribution | null {
  const { session, contributions, sessionTrackName, trackId } = options;
  const assignable = listAssignableCircuitContributions(contributions);
  if (assignable.length === 0) {
    return null;
  }

  if (session) {
    const gpsMatch = matchSessionToCircuitDraft(session, assignable);
    if (gpsMatch) {
      return findTrackContributionById(assignable, gpsMatch.draftId);
    }

    const center = sessionGpsCenter(session);
    if (center) {
      const nearby = findNearestContributionByLocation(assignable, center);
      if (nearby) {
        return nearby;
      }
    }
  }

  const normalizedTrack = normalizeCircuitVenueName(sessionTrackName);
  if (normalizedTrack) {
    const exactVenueMatches = assignable.filter(
      (contribution) => normalizeCircuitVenueName(contribution.venue) === normalizedTrack,
    );
    if (exactVenueMatches.length > 0) {
      return exactVenueMatches[0] ?? null;
    }

    const partialMatch = assignable.find((contribution) => {
      const venue = contribution.venue?.trim() ?? '';
      return venue && venuesAlign(normalizedTrack, venue);
    });
    if (partialMatch) {
      return partialMatch;
    }
  }

  return findBestContributionForTrack(assignable, trackId ?? null);
}

export type SessionCircuitPickerEntry = {
  contribution: TrackContribution;
  distanceKm: number | null;
  recent: boolean;
};

const PICKER_RECENT_LIMIT = 6;
const PICKER_RECENT_MAX_AGE_MS = 30 * 24 * 60 * 60 * 1000;
const PICKER_NEARBY_MAX_KM = 8;

function contributionDistanceKm(
  contribution: TrackContribution,
  center: { lat: number; lon: number } | null,
): number | null {
  if (!center) {
    return null;
  }

  const location = contribution.location;
  if (!location) {
    return null;
  }

  return trackDistanceKm(
    {
      id: contribution.trackId,
      teamId: contribution.teamId,
      name: contribution.venue ?? '',
      centerLat: location.centerLat,
      centerLon: location.centerLon,
      createdAt: contribution.createdAt,
      updatedAt: contribution.updatedAt,
    },
    center.lat,
    center.lon,
  );
}

function pinRank(
  contributionId: string,
  options: {
    assignedContributionId?: string | null;
    suggestedId?: string | null;
    trackMatchId?: string | null;
  },
) {
  if (contributionId === options.assignedContributionId) {
    return 0;
  }

  if (contributionId === options.suggestedId) {
    return 1;
  }

  if (contributionId === options.trackMatchId) {
    return 2;
  }

  return 10;
}

export function rankSessionCircuitPickerContributions(
  contributions: TrackContribution[] | undefined,
  options: {
    assignedContributionId?: string | null;
    session?: MyChronSessionExport | null;
    sessionGps?: Array<{ lat: number; lon: number }> | null;
    sessionTrackName?: string | null;
    trackId?: string | null;
    limit?: number;
  } = {},
): SessionCircuitPickerEntry[] {
  const assignable = listAssignableCircuitContributions(contributions);
  if (assignable.length === 0) {
    return [];
  }

  const limit = options.limit ?? 16;
  const center = sessionPickerCenter(options);
  const suggested = suggestCircuitContributionForSession({
    session: options.session ?? null,
    contributions: assignable,
    sessionTrackName: options.sessionTrackName,
    trackId: options.trackId,
  });
  const trackMatch = findBestContributionForTrack(assignable, options.trackId);
  const pinnedIds = new Set(
    [options.assignedContributionId, suggested?.id, trackMatch?.id].filter(Boolean),
  );

  const scored = assignable.map((contribution) => ({
    contribution,
    distanceKm: contributionDistanceKm(contribution, center),
    updatedAtMs: Date.parse(contribution.updatedAt) || 0,
  }));

  const now = Date.now();
  const recentCandidates = [...scored]
    .filter((entry) => now - entry.updatedAtMs <= PICKER_RECENT_MAX_AGE_MS)
    .sort((left, right) => right.updatedAtMs - left.updatedAtMs)
    .slice(0, PICKER_RECENT_LIMIT);
  const recentIds = new Set(recentCandidates.map((entry) => entry.contribution.id));

  const pool = new Map<string, SessionCircuitPickerEntry>();
  const addEntry = (entry: (typeof scored)[number], flags: { recent?: boolean } = {}) => {
    if (pool.has(entry.contribution.id)) {
      return;
    }

    pool.set(entry.contribution.id, {
      contribution: entry.contribution,
      distanceKm: entry.distanceKm,
      recent: flags.recent ?? recentIds.has(entry.contribution.id),
    });
  };

  for (const contributionId of pinnedIds) {
    const entry = scored.find((candidate) => candidate.contribution.id === contributionId);
    if (entry) {
      addEntry(entry);
    }
  }

  for (const entry of recentCandidates) {
    if (pool.size >= limit) {
      break;
    }
    addEntry(entry, { recent: true });
  }

  for (const entry of [...scored].sort((left, right) => {
    if (left.distanceKm != null && right.distanceKm != null) {
      return left.distanceKm - right.distanceKm;
    }

    if (left.distanceKm != null) {
      return -1;
    }

    if (right.distanceKm != null) {
      return 1;
    }

    return right.updatedAtMs - left.updatedAtMs;
  })) {
    if (pool.size >= limit) {
      break;
    }

    if (
      center &&
      entry.distanceKm != null &&
      entry.distanceKm > PICKER_NEARBY_MAX_KM &&
      !pinnedIds.has(entry.contribution.id) &&
      !recentIds.has(entry.contribution.id)
    ) {
      continue;
    }

    addEntry(entry);
  }

  if (pool.size < limit) {
    for (const entry of [...scored].sort((left, right) => right.updatedAtMs - left.updatedAtMs)) {
      if (pool.size >= limit) {
        break;
      }
      addEntry(entry);
    }
  }

  const pinOptions = {
    assignedContributionId: options.assignedContributionId,
    suggestedId: suggested?.id ?? null,
    trackMatchId: trackMatch?.id ?? null,
  };

  return [...pool.values()]
    .sort((left, right) => {
      const leftPin = pinRank(left.contribution.id, pinOptions);
      const rightPin = pinRank(right.contribution.id, pinOptions);
      if (leftPin !== rightPin) {
        return leftPin - rightPin;
      }

      if (left.distanceKm != null && right.distanceKm != null) {
        if (left.distanceKm !== right.distanceKm) {
          return left.distanceKm - right.distanceKm;
        }
      } else if (left.distanceKm != null) {
        return -1;
      } else if (right.distanceKm != null) {
        return 1;
      }

      if (left.recent !== right.recent) {
        return left.recent ? -1 : 1;
      }

      return Date.parse(right.contribution.updatedAt) - Date.parse(left.contribution.updatedAt);
    })
    .slice(0, limit);
}

export function listAssignableCircuitContributions(
  contributions: TrackContribution[] | undefined,
): TrackContribution[] {
  if (!contributions?.length) {
    return [];
  }

  const statusRank: Record<TrackContribution['status'], number> = {
    merged: 0,
    accepted: 1,
    reviewed: 2,
    pending: 3,
    draft: 4,
    rejected: 5,
  };

  return contributions.filter(contributionHasOverlayGeometry).sort((left, right) => {
    const rankDiff = statusRank[left.status] - statusRank[right.status];
    if (rankDiff !== 0) {
      return rankDiff;
    }

    const leftName = left.venue?.trim() || '';
    const rightName = right.venue?.trim() || '';
    const nameDiff = leftName.localeCompare(rightName);
    if (nameDiff !== 0) {
      return nameDiff;
    }

    return Date.parse(right.updatedAt) - Date.parse(left.updatedAt);
  });
}

export function resolveCircuitOverlayForSession(options: {
  contributions: TrackContribution[] | undefined;
  requestedContributionId: string | null;
  assignedContributionId: string | null | undefined;
  trackId: string | null | undefined;
  savedCircuitLapSet: SavedCircuitLapSet | null | undefined;
  session: MyChronSessionExport | null;
}): ResolvedCircuitOverlay {
  const {
    contributions,
    requestedContributionId,
    assignedContributionId,
    trackId,
    savedCircuitLapSet,
    session,
  } = options;

  if (!contributions?.length) {
    return { overlay: null, source: null, contributionId: null };
  }

  if (requestedContributionId) {
    const fromUrl = findTrackContributionById(contributions, requestedContributionId);
    if (fromUrl) {
      return {
        overlay: buildCircuitMapOverlay(fromUrl, session),
        source: 'url',
        contributionId: fromUrl.id,
      };
    }
  }

  if (assignedContributionId) {
    const fromAssigned = findTrackContributionById(contributions, assignedContributionId);
    if (fromAssigned) {
      return {
        overlay: buildCircuitMapOverlay(fromAssigned, session),
        source: 'assigned',
        contributionId: fromAssigned.id,
      };
    }

    // An explicit assignment is authoritative. Do not fall back to saved laps
    // from a prior contribution when the assigned contribution is unavailable.
    return { overlay: null, source: null, contributionId: null };
  }

  const savedContributionId = savedCircuitLapSet?.contributionId ?? null;
  if (savedContributionId) {
    const fromSaved = findTrackContributionById(contributions, savedContributionId);
    if (fromSaved) {
      return {
        overlay: buildCircuitMapOverlay(fromSaved, session),
        source: 'saved-laps',
        contributionId: fromSaved.id,
      };
    }
  }

  if (session) {
    const match = matchSessionToCircuitDraft(session, contributions);
    if (match) {
      const fromMatch = findTrackContributionById(contributions, match.draftId);
      if (fromMatch) {
        return {
          overlay: buildCircuitMapOverlay(fromMatch, session),
          source: 'gps-match',
          contributionId: fromMatch.id,
        };
      }
    }
  }

  const fromTrack = findBestContributionForTrack(contributions, trackId);
  if (fromTrack) {
    return {
      overlay: buildCircuitMapOverlay(fromTrack, session),
      source: 'track',
      contributionId: fromTrack.id,
    };
  }

  return { overlay: null, source: null, contributionId: null };
}

export function buildTelemetryCircuitUrl(options: {
  contributionId: string;
  sessionKey?: string | null;
  lapNumber?: number | null;
}) {
  const params = new URLSearchParams({ circuit: options.contributionId });
  if (options.sessionKey) {
    params.set('session', options.sessionKey);
  }
  if (options.lapNumber != null && Number.isFinite(options.lapNumber)) {
    params.set('lap', String(options.lapNumber));
  }
  return `/sessions?${params.toString()}`;
}
